import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuRotateCcw, LuSearch, LuX } from 'react-icons/lu';
import { MdFilterAlt, MdOutlineHourglassEmpty } from 'react-icons/md';
import { DateRangePicker, TwDropdown } from 'components/common';

// Mirrors LogisticTrackingFilterBar so the CSD plan and the Logistic tracking
// screens read as one toolbar. `value` is the API's `quick_filter`.
export const STATUS_OPTIONS = [
  { value: '', label: 'All statuses', icon: MdFilterAlt },
  { value: 'not_yet_shipped', label: 'Not shipped', icon: MdOutlineHourglassEmpty },
];

const STATUS_ICONS = Object.fromEntries(STATUS_OPTIONS.map(({ value, icon }) => [value, icon]));

const FOCUS_RING = 'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-1';

const Divider = () => (
  <div className="tw-hidden xl:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" aria-hidden="true" />
);

const ShipmentPlanFilterBar = ({ filters, onChange, onReset, onOpenAdvanced, advancedCount }) => {
  const activeCount = (filters.etdRange?.startDate ? 1 : 0)
    + (filters.search ? 1 : 0)
    + (filters.status ? 1 : 0)
    + advancedCount;

  return (
    <div role="search" className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-px-3 tw-py-2.5">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2">
        <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-60 sm:tw-flex-shrink-0">
          <LuSearch className="tw-absolute tw-left-2.5 tw-text-slate-400 tw-pointer-events-none" size={13} aria-hidden="true" />
          <input
            type="search"
            name="shipment_plan_search"
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
            value={filters.search}
            onChange={event => onChange({ search: event.target.value })}
            placeholder="Job#, project name, phase…"
            aria-label="Search by job number, project name or phase name"
            className={`tw-w-full tw-h-8 tw-pl-8 tw-pr-7 tw-text-xs tw-border tw-border-solid tw-border-slate-300 tw-rounded tw-bg-white tw-text-slate-700 tw-placeholder-slate-400 ${FOCUS_RING}`}
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => onChange({ search: '' })}
              aria-label="Clear search"
              className={`tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0 tw-touch-manipulation ${FOCUS_RING}`}
            >
              <LuX size={9} />
            </button>
          )}
        </div>

        <Divider />

        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0" title="CSD ETD">
          <LuCalendarClock size={13} className="tw-text-slate-400" aria-hidden="true" />
          <span className="tw-text-xs tw-font-medium tw-text-slate-500 tw-whitespace-nowrap">ETD</span>
          <DateRangePicker
            label="Any"
            startDate={filters.etdRange?.startDate || ''}
            endDate={filters.etdRange?.endDate || ''}
            onChange={({ startDate, endDate }) => onChange({ etdRange: { startDate, endDate } })}
          />
        </div>

        <Divider />

        <TwDropdown
          size="sm"
          value={filters.status}
          onChange={value => onChange({ status: value })}
          options={STATUS_OPTIONS}
          leftIcon={STATUS_ICONS[filters.status] || MdFilterAlt}
          placeholder="All statuses"
          minWidth="150px"
          menuWidth="200px"
        />

        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto tw-flex-shrink-0">
          <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-hidden sm:tw-block" aria-live="polite">
            {activeCount === 0 ? 'No filter applied' : `${activeCount} filter${activeCount === 1 ? '' : 's'} applied`}
          </p>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className={`tw-inline-flex tw-items-center tw-gap-1 tw-h-8 tw-px-2 tw-rounded tw-border-0 tw-bg-transparent tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-text-red-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-touch-manipulation ${FOCUS_RING}`}
            >
              <LuRotateCcw size={12} aria-hidden="true" /> Clear all
            </button>
          )}
          <button
            type="button"
            onClick={onOpenAdvanced}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-rounded tw-border tw-border-solid tw-text-xs tw-font-semibold tw-cursor-pointer tw-whitespace-nowrap tw-touch-manipulation tw-transition-colors ${FOCUS_RING} ${
              advancedCount > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-border-primary-400 hover:tw-text-primary-600'
            }`}
          >
            <MdFilterAlt size={14} aria-hidden="true" />
            More filters
            {advancedCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[16px] tw-h-4 tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-[10px] tw-font-bold tw-leading-none tw-tabular-nums">
                {advancedCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

ShipmentPlanFilterBar.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    etdRange: PropTypes.object,
    status: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onOpenAdvanced: PropTypes.func.isRequired,
  advancedCount: PropTypes.number,
};

ShipmentPlanFilterBar.defaultProps = { advancedCount: 0 };

export default ShipmentPlanFilterBar;
